import { useState, useRef } from "react";
import { BsFileEarmarkExcelFill } from "../../../assets/icons/vander";
import { usePatientsStore } from "../../../hooks";
import { useFormMessages } from "../../hooks";
import {
  ErrorMessage,
  SuccessMessage,
  LoadingData,
  CancelButtonOutline,
} from "../../ui";
import { BulkUploadPatientsTable } from "../tables";

export const UploadCsvCard = () => {
  // Datos del hook ----------------------------------------//
  const {
    // Props
    isLoading,
    message,

    // Methods
    startUploadCsv,
  } = usePatientsStore();

  // States ------------------------------------------------//
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [uploadResults, setUploadResults] = useState(null);
  const fileInputRef = useRef(null);

  // Formulario --------------------------------------------//
  const {
    setFormSubmitted,
    showErrorMessage,
    showSuccessMessage,
    messageText,
  } = useFormMessages({
    message,
    onResetForm: () => {
      setCsvFile(null);
      setCsvData([]);
      setUploadResults(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
  });

  // Funciones ---------------------------------------------//
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type === "text/csv" || file.name.endsWith(".csv")) {
        setFormSubmitted(false);
        setCsvFile(file);
        setUploadResults(null);
        parseCsvFile(file);
      } else {
        setFormSubmitted(true);
        startUploadCsv(null, "Por favor selecciona un archivo CSV válido");
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    }
  };

  const parseCsvFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split("\n").filter((line) => line.trim());

        if (lines.length < 2) {
          throw new Error(
            "El archivo CSV debe tener al menos una fila de datos"
          );
        }

        const headers = lines[0].split(",");
        if (headers.length < 19) {
          throw new Error(
            "El archivo CSV no tiene todas las columnas requeridas"
          );
        }

        const data = lines.slice(1).map((line, index) => {
          const values = line.split(",");
          if (values.length < 4) {
            throw new Error(`Fila ${index + 2}: Faltan datos obligatorios`);
          }
          return {
            firstName: values[0] || "",
            lastName: values[1] || "",
            documentType: values[2] || "",
            document: values[3] || "",
            gender: values[4] || "",
            birthDate: values[5] || "",
            ratePlan: values[6] || "",
            ethnicity: values[7] || "",
            maritalStatus: values[8] || "",
            country: values[9] || "",
            department: values[10] || "",
            city: values[11] || "",
            address: values[12] || "",
            birthCountry: values[13] || "",
            birthDepartment: values[14] || "",
            birthCity: values[15] || "",
            countryPhone: values[16] || "",
            phoneNumber: values[17] || "",
            email: values[18] || "",
          };
        });

        setCsvData(data);
      } catch (error) {
        setFormSubmitted(true);
        startUploadCsv(
          null,
          error.message || "Error al procesar el archivo CSV"
        );
        setUploadResults(null);
        setCsvData([]);
      }
    };
    reader.readAsText(file);
  };

  const handleCsvUpload = async () => {
    if (!csvFile) return;
    setFormSubmitted(true);
    const result = await startUploadCsv(csvFile);
    if (result?.success && result?.data) {
      setUploadResults(result.data);
    }
  };

  const handleCancelFile = () => {
    setCsvFile(null);
    setCsvData([]);
    setUploadResults(null);
    setFormSubmitted(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };
  // Efectos -----------------------------------------------//

  return (
    <div className="card border-0 rounded shadow">
      <div className="card-body">
        <div className="vstack gap-2">
          <h6 className="mb-0">Registrar pacientes masivamente</h6>
          <div className="d-flex gap-3 align-items-center mb-2">
            <input
              type="file"
              name="files"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileChange}
              accept=".csv"
            />
            <button
              className="btn btn-success px-2-5 py-1-5"
              onClick={
                csvFile ? handleCsvUpload : () => fileInputRef.current.click()
              }
              disabled={isLoading}
            >
              {csvFile ? (
                isLoading ? (
                  "Registrando..."
                ) : (
                  "Registrar pacientes"
                )
              ) : (
                <>
                  Subir archivo CSV{" "}
                  <BsFileEarmarkExcelFill className="icons ms-2" />
                </>
              )}
            </button>
            {csvFile && (
              <CancelButtonOutline
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleCancelFile}
                disabled={isLoading}
              >
                Cancelar
              </CancelButtonOutline>
            )}
          </div>

          {isLoading && <LoadingData />}
          {/* Mensajes del store */}
          {showErrorMessage && <ErrorMessage>{messageText}</ErrorMessage>}
          {showSuccessMessage && <SuccessMessage>{messageText}</SuccessMessage>}

          <BulkUploadPatientsTable
            title={`Vista previa (${csvData.length} pacientes)`}
            data={csvData}
            headerClass="table-primary"
          />

          {uploadResults && (
            <>
              <div className="table-responsive mt-2">
                <table className="table table-sm table-striped">
                  <thead className="table-primary">
                    <tr>
                      <th colSpan="2" className="text-center">
                        Resultados del registro masivo
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="fw-bold">Total registros</td>
                      <td>{uploadResults.totalRecords}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Registrados</td>
                      <td>{uploadResults.registeredPatients?.length || 0}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Inválidos</td>
                      <td>{uploadResults.invalidRecords?.length || 0}</td>
                    </tr>
                    <tr>
                      <td className="fw-bold">Fallidos</td>
                      <td>{uploadResults.failedPatients?.length || 0}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <BulkUploadPatientsTable
                title={`Pacientes registrados exitosamente (${
                  uploadResults.registeredPatients?.length || 0
                })`}
                data={uploadResults.registeredPatients}
                headerClass="table-success"
                rowKey="id"
              />

              <BulkUploadPatientsTable
                title={`Registros inválidos (${
                  uploadResults.invalidRecords?.length || 0
                })`}
                data={uploadResults.invalidRecords?.map((invalid) => ({
                  ...invalid.record,
                  "#": invalid.row,
                  error: invalid.error,
                }))}
                headerClass="table-warning"
              />

              <BulkUploadPatientsTable
                title={`Pacientes fallidos (${
                  uploadResults.failedPatients?.length || 0
                })`}
                data={uploadResults.failedPatients?.map((failed) => ({
                  ...failed.data,
                  "#": failed.row,
                  error: failed.error,
                }))}
                headerClass="table-danger"
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};
